import { type ReactNode, useState } from "react"
import { Box, Collapse, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { type IconProp } from "@fortawesome/fontawesome-svg-core"

import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import type { TranslationKey } from "../../../../../localization/translations"

import { PanelHeaderActions } from "./PanelHeaderActions"
import { Stack } from "@mui/system"

type StyleProps = Readonly<{
    iconBackgroundColor: string
    iconColor: string
}>

const useStyles = makeStyles<StyleProps>()((theme, { iconBackgroundColor, iconColor }) => ({
    panel: {
        border: `1px solid ${theme.palette.tailwind.gray[200]}`,
        boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.08)",
        borderRadius: "1rem",
        padding: "1.25rem 1.5rem",
        background: theme.palette.common.white,
    },
    header: {
        display: "flex",
        alignItems: "center",
    },
    iconBox: {
        width: "2.5rem",
        height: "2.5rem",
        borderRadius: ".5rem",
        background: iconBackgroundColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginRight: "1rem",
    },
    headerIcon: {
        color: iconColor,
        fontSize: "1.25rem",
    },
    title: {
        color: theme.palette.text.primary,
        fontWeight: 500,
        marginRight: ".75rem",
    },
    counter: {
        minWidth: "2rem",
        height: "2rem",
        padding: "0 .5rem",
        borderRadius: "1rem",
        background: theme.palette.tailwind.slate[100],
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    counterText: {
        color: theme.palette.tailwind.slate[500],
        fontSize: "1rem",
        fontWeight: 500,
    },
    spacer: {
        flex: 1,
    },
    body: {
        paddingTop: "1.5rem",
    },
}))

type UpcomingOrdersPanelProps = Readonly<{
    icon: IconProp
    iconBackgroundColor: string
    iconColor: string
    titleId: TranslationKey
    titleFallback: string
    counter: number
    onShowAll?: () => void
    collapseAriaLabel: string
    expandAriaLabel: string
    children: ReactNode
    footer?: ReactNode
}>

export function UpcomingOrdersPanel({
    icon,
    iconBackgroundColor,
    iconColor,
    titleId,
    titleFallback,
    counter,
    onShowAll,
    collapseAriaLabel,
    expandAriaLabel,
    children,
    footer,
}: UpcomingOrdersPanelProps) {
    const { classes } = useStyles({ iconBackgroundColor, iconColor })
    const [isOpen, setIsOpen] = useState(true)

    return (
        <Box className={classes.panel}>
            <Box className={classes.header}>
                <Box className={classes.iconBox}>
                    <FontAwesomeIcon icon={icon} className={classes.headerIcon} />
                </Box>
                <LocalizedStrict id={titleId}>
                    <Typography variant="h5" className={classes.title}>
                        {titleFallback}
                    </Typography>
                </LocalizedStrict>
                <Box className={classes.counter}>
                    <Typography variant="h6" className={classes.counterText}>
                        {counter}
                    </Typography>
                </Box>
                <Box className={classes.spacer} />
                <PanelHeaderActions
                    onShowAll={onShowAll}
                    isOpen={isOpen}
                    onToggle={() => setIsOpen((prev) => !prev)}
                    collapseAriaLabel={collapseAriaLabel}
                    expandAriaLabel={expandAriaLabel}
                />
            </Box>
            <Collapse in={isOpen}>
                <Stack spacing="1rem">
                    <Box className={classes.body}>{children}</Box>
                    {footer}
                </Stack>
            </Collapse>
        </Box>
    )
}
